import React, { useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import "./index.scss";

const ExceptionMaster = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [formData, setFormData] = useState({
    amountVarianceThreshold: 100.00,
    percentVarianceThreshold: 5,
    dateVarianceDays: 30,
    duplicateCheckPeriod: 90,
    autoResolveLimit: 1000.00,
    writeOffLimit: 50.00,
    adjustmentApprovalLimit: 5000.00,
    exceptionHoldingAccount: "",
    writeOffAccount: "",
    adjustmentAccount: "",
    varianceAccount: ""
  });

  const [exceptionTypes, setExceptionTypes] = useState([
    {
      exceptionCode: "EXC001",
      exceptionName: "Amount Mismatch",
      category: "Amount Mismatch",
      severity: "High",
      autoResolve: false,
      resolutionAction: "Manual Review",
      notifyRole: "Supervisor",
      active: true
    },
    {
      exceptionCode: "EXC002",
      exceptionName: "Missing Policy Document",
      category: "Missing Document",
      severity: "Medium",
      autoResolve: false,
      resolutionAction: "Hold for Approval",
      notifyRole: "Manager",
      active: true
    },
    {
      exceptionCode: "EXC003",
      exceptionName: "Duplicate Payment",
      category: "Duplicate Entry",
      severity: "Critical",
      autoResolve: true,
      resolutionAction: "Auto-Adjust",
      notifyRole: "Finance Head",
      active: true
    },
    {
      exceptionCode: "EXC004",
      exceptionName: "Date Discrepancy",
      category: "Date Discrepancy",
      severity: "Low",
      autoResolve: true,
      resolutionAction: "Auto-Adjust",
      notifyRole: "User",
      active: true
    }
  ]);

  const [routingMatrix, setRoutingMatrix] = useState({
    "Low": {
      "Amount Mismatch": "User",
      "Missing Document": "User",
      "Date Discrepancy": "User",
      "Duplicate Entry": "Supervisor",
      "System Error": "User"
    },
    "Medium": {
      "Amount Mismatch": "Supervisor",
      "Missing Document": "Supervisor",
      "Date Discrepancy": "User",
      "Duplicate Entry": "Supervisor",
      "System Error": "Supervisor"
    },
    "High": {
      "Amount Mismatch": "Manager",
      "Missing Document": "Manager",
      "Date Discrepancy": "Supervisor",
      "Duplicate Entry": "Manager",
      "System Error": "Manager"
    },
    "Critical": {
      "Amount Mismatch": "Finance Head",
      "Missing Document": "Finance Head",
      "Date Discrepancy": "Manager",
      "Duplicate Entry": "Finance Head",
      "System Error": "Finance Head"
    }
  });

  const categoryOptions = [
    { label: "Amount Mismatch", value: "Amount Mismatch" },
    { label: "Missing Document", value: "Missing Document" },
    { label: "Date Discrepancy", value: "Date Discrepancy" },
    { label: "Duplicate Entry", value: "Duplicate Entry" },
    { label: "System Error", value: "System Error" },
    { label: "Policy Not Found", value: "Policy Not Found" },
    { label: "Commission Error", value: "Commission Error" }
  ];

  const severityOptions = [
    { label: "Low", value: "Low" },
    { label: "Medium", value: "Medium" },
    { label: "High", value: "High" },
    { label: "Critical", value: "Critical" }
  ];

  const resolutionActionOptions = [
    { label: "Manual Review", value: "Manual Review" },
    { label: "Auto-Adjust", value: "Auto-Adjust" },
    { label: "Create Adjustment", value: "Create Adjustment" },
    { label: "Reject", value: "Reject" },
    { label: "Hold for Approval", value: "Hold for Approval" }
  ];

  const roleOptions = [
    { label: "User", value: "User" },
    { label: "Supervisor", value: "Supervisor" },
    { label: "Manager", value: "Manager" },
    { label: "Finance Head", value: "Finance Head" },
    { label: "CFO", value: "CFO" }
  ];

  const onCellEdit = (options) => {
    let updatedData = [...options.props.value];
    updatedData[options.rowIndex][options.field] = options.newValue;
    return updatedData;
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" tooltip="Edit" />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" tooltip="Delete" />
      </div>
    );
  };

  const checkboxBodyTemplate = (rowData, column) => {
    return <Checkbox checked={rowData[column.field]} disabled />;
  };

  return (
    <div className="exception-master">
      <Card title="Remittance Exception Master">
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel header="Exception Types">
            <div className="toolbar mb-3">
              <Button label="Add Exception Type" icon="pi pi-plus" className="mr-2" />
              <Button label="Import" icon="pi pi-upload" className="p-button-secondary" />
            </div>
            <DataTable value={exceptionTypes} editMode="cell" stripedRows>
              <Column field="exceptionCode" header="Code" editor={(props) =>
                <InputText value={props.value} onChange={(e) => props.editorCallback(e.target.value)} />
              } style={{ width: '10%' }} />
              <Column field="exceptionName" header="Exception Name" editor={(props) =>
                <InputText value={props.value} onChange={(e) => props.editorCallback(e.target.value)} />
              } style={{ width: '20%' }} />
              <Column field="category" header="Category" editor={(props) =>
                <Dropdown value={props.value} options={categoryOptions} onChange={(e) => props.editorCallback(e.value)} />
              } style={{ width: '15%' }} />
              <Column field="severity" header="Severity" editor={(props) =>
                <Dropdown value={props.value} options={severityOptions} onChange={(e) => props.editorCallback(e.value)} />
              } style={{ width: '10%' }} />
              <Column field="autoResolve" header="Auto-Resolve" body={checkboxBodyTemplate} editor={(props) =>
                <Checkbox checked={props.value} onChange={(e) => props.editorCallback(e.checked)} />
              } style={{ width: '10%' }} />
              <Column field="resolutionAction" header="Resolution Action" editor={(props) =>
                <Dropdown value={props.value} options={resolutionActionOptions} onChange={(e) => props.editorCallback(e.value)} />
              } style={{ width: '15%' }} />
              <Column field="notifyRole" header="Notify" editor={(props) =>
                <Dropdown value={props.value} options={roleOptions} onChange={(e) => props.editorCallback(e.value)} />
              } style={{ width: '10%' }} />
              <Column field="active" header="Active" body={checkboxBodyTemplate} editor={(props) =>
                <Checkbox checked={props.value} onChange={(e) => props.editorCallback(e.checked)} />
              } style={{ width: '5%' }} />
              <Column header="" body={actionsBodyTemplate} style={{ width: '5%' }} />
            </DataTable>
          </TabPanel>

          <TabPanel header="Thresholds">
            <div className="form-section">
              <h3>Exception Thresholds</h3>
              <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col-12 p-md-6">
                  <label>Amount Variance Threshold</label>
                  <InputNumber
                    value={formData.amountVarianceThreshold}
                    onValueChange={(e) => setFormData({ ...formData, amountVarianceThreshold: e.value })}
                    mode="currency"
                    currency="USD"
                  />
                  <small>Minimum variance to trigger exception</small>
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Percentage Variance Threshold (%)</label>
                  <InputNumber
                    value={formData.percentVarianceThreshold}
                    onValueChange={(e) => setFormData({ ...formData, percentVarianceThreshold: e.value })}
                    suffix="%"
                  />
                  <small>Minimum percentage variance</small>
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Date Variance (Days)</label>
                  <InputNumber
                    value={formData.dateVarianceDays}
                    onValueChange={(e) => setFormData({ ...formData, dateVarianceDays: e.value })}
                  />
                  <small>Days difference to trigger exception</small>
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Duplicate Check Period (Days)</label>
                  <InputNumber
                    value={formData.duplicateCheckPeriod}
                    onValueChange={(e) => setFormData({ ...formData, duplicateCheckPeriod: e.value })}
                  />
                </div>
              </div>

              <h3>Auto-Resolution Limits</h3>
              <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col-12 p-md-4">
                  <label>Auto-Resolve Amount Limit</label>
                  <InputNumber
                    value={formData.autoResolveLimit}
                    onValueChange={(e) => setFormData({ ...formData, autoResolveLimit: e.value })}
                    mode="currency"
                    currency="USD"
                  />
                </div>
                <div className="p-field p-col-12 p-md-4">
                  <label>Write-off Limit</label>
                  <InputNumber
                    value={formData.writeOffLimit}
                    onValueChange={(e) => setFormData({ ...formData, writeOffLimit: e.value })}
                    mode="currency"
                    currency="USD"
                  />
                </div>
                <div className="p-field p-col-12 p-md-4">
                  <label>Adjustment Approval Limit</label>
                  <InputNumber
                    value={formData.adjustmentApprovalLimit}
                    onValueChange={(e) => setFormData({ ...formData, adjustmentApprovalLimit: e.value })}
                    mode="currency"
                    currency="USD"
                  />
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="GL Mapping">
            <div className="form-section">
              <h3>Exception GL Accounts</h3>
              <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col-12 p-md-6">
                  <label>Exception Holding Account *</label>
                  <div className="p-inputgroup">
                    <InputText
                      value={formData.exceptionHoldingAccount}
                      onChange={(e) => setFormData({ ...formData, exceptionHoldingAccount: e.target.value })}
                    />
                    <Button icon="pi pi-search" />
                  </div>
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Write-off Account *</label>
                  <div className="p-inputgroup">
                    <InputText
                      value={formData.writeOffAccount}
                      onChange={(e) => setFormData({ ...formData, writeOffAccount: e.target.value })}
                    />
                    <Button icon="pi pi-search" />
                  </div>
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Adjustment Account *</label>
                  <div className="p-inputgroup">
                    <InputText
                      value={formData.adjustmentAccount}
                      onChange={(e) => setFormData({ ...formData, adjustmentAccount: e.target.value })}
                    />
                    <Button icon="pi pi-search" />
                  </div>
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Variance Account *</label>
                  <div className="p-inputgroup">
                    <InputText
                      value={formData.varianceAccount}
                      onChange={(e) => setFormData({ ...formData, varianceAccount: e.target.value })}
                    />
                    <Button icon="pi pi-search" />
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Routing Rules">
            <div className="form-section">
              <h3>Exception Routing Matrix</h3>
              <p>Configure which role handles exceptions based on severity and category</p>
              <DataTable value={Object.keys(routingMatrix)} stripedRows>
                <Column field="severity" header="Severity" body={(rowData) => rowData} style={{ width: '15%' }} />
                <Column header="Amount Mismatch" body={(rowData) =>
                  <Dropdown
                    value={routingMatrix[rowData]["Amount Mismatch"]}
                    options={roleOptions}
                    onChange={(e) => {
                      const newMatrix = { ...routingMatrix };
                      newMatrix[rowData]["Amount Mismatch"] = e.value;
                      setRoutingMatrix(newMatrix);
                    }}
                  />
                } />
                <Column header="Missing Document" body={(rowData) =>
                  <Dropdown
                    value={routingMatrix[rowData]["Missing Document"]}
                    options={roleOptions}
                    onChange={(e) => {
                      const newMatrix = { ...routingMatrix };
                      newMatrix[rowData]["Missing Document"] = e.value;
                      setRoutingMatrix(newMatrix);
                    }}
                  />
                } />
                <Column header="Date Discrepancy" body={(rowData) =>
                  <Dropdown
                    value={routingMatrix[rowData]["Date Discrepancy"]}
                    options={roleOptions}
                    onChange={(e) => {
                      const newMatrix = { ...routingMatrix };
                      newMatrix[rowData]["Date Discrepancy"] = e.value;
                      setRoutingMatrix(newMatrix);
                    }}
                  />
                } />
                <Column header="Duplicate Entry" body={(rowData) =>
                  <Dropdown
                    value={routingMatrix[rowData]["Duplicate Entry"]}
                    options={roleOptions}
                    onChange={(e) => {
                      const newMatrix = { ...routingMatrix };
                      newMatrix[rowData]["Duplicate Entry"] = e.value;
                      setRoutingMatrix(newMatrix);
                    }}
                  />
                } />
                <Column header="System Error" body={(rowData) =>
                  <Dropdown
                    value={routingMatrix[rowData]["System Error"]}
                    options={roleOptions}
                    onChange={(e) => {
                      const newMatrix = { ...routingMatrix };
                      newMatrix[rowData]["System Error"] = e.value;
                      setRoutingMatrix(newMatrix);
                    }}
                  />
                } />
              </DataTable>
            </div>
          </TabPanel>
        </TabView>

        <div className="button-group mt-4">
          <Button label="Save" icon="pi pi-check" className="p-button-success" />
          <Button label="Cancel" icon="pi pi-times" className="p-button-secondary ml-2" />
        </div>
      </Card>
    </div>
  );
};

export default ExceptionMaster;